import React, { useEffect, useState } from "react";
import { NamedProperty, ensure } from "sprint-common-web";

import { UserItem } from "../listing/UsersListing";
import { useAdminUsersApi } from "../useAdminUsersApi";
import { UserListingContext } from "./UserListingContext";

interface UserListingProviderProps {
    children: React.ReactNode;
}

export const UserListingProvider: React.FC<UserListingProviderProps> = ({ children }: UserListingProviderProps) => {
    const { getUsersList, getOriginatorsLookup } = useAdminUsersApi();

    const [userItems, setUserItems] = useState<UserItem[]>([]);
    const [originators, setOriginators] = useState<NamedProperty[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const loadUsers = async () => {
        const users = await getUsersList();
        const originatorsLookupResponse = await getOriginatorsLookup();

        const mappedOriginators = originatorsLookupResponse.items.map((p) => {
            return { id: p.id, name: p.name };
        });

        const mappedUsers = users.items.map((user) => {
            const filteredOriginators = mappedOriginators.filter((p) => user.originatorIds.includes(p.id));

            return {
                name: user.name,
                id: user.name,
                isModified: false,
                originators: filteredOriginators.map((p) => {
                    return { id: p.id, name: p.name };
                })
            };
        });
        setOriginators(mappedOriginators);
        setUserItems(mappedUsers);
    };

    const updateUser = (name: string, originatorIds: string[]): void => {
        const userToUpdate = userItems.find((p) => p.name === name);
        ensure(userToUpdate);
        const orgs = originators.filter((p) => originatorIds.includes(p.id));

        userToUpdate.originators = orgs;
        userToUpdate.isModified = true;

        return setUserItems([...userItems]);
    };

    useEffect(() => {
        setIsLoading(true);

        loadUsers().finally(() => {
            setIsLoading(false);
        });
    }, []);

    return (
        <UserListingContext.Provider value={{ userItems, originators, isLoading, updateUser }}>
            {children}
        </UserListingContext.Provider>
    );
};
